/**
 * Every newsletter that carries a prayer request, browsable on its own.
 *
 * `recent` opens it as **Current Prayer Needs** instead: the same page, seeded to the
 * starred updates of the last thirty days, which is the reading somebody wants when they
 * are looking for something to pray about now rather than searching the corpus. It has
 * its own route and its own sidebar entry because a link cannot carry that seeding — the
 * thirty days move, and a `to` written into a module-level nav array would be thirty days
 * from whenever the bundle loaded.
 *
 * Seeded, not locked: every filter is still there and the window can be cleared like any
 * other, which is why this is a variant of this page rather than a page of its own.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs, fetchAllRows } from '../lib/api'
import { PartnerSelect, CountrySelect, EnumSelect, YearSelect } from '../components/Select'
import { DateRangeField, DatePresets, toIso } from '../components/DateField'
import { PrayerText } from '../components/RichText'
import { DataTable, ViewToggle, useViewMode, SortBar } from '../components/DataTable'
import {
  Card, Field, Badge, SearchInput, Pager, Empty, Loading, ErrorState, Filters, FilterActions, countFilters,
  useSort, fmtInt, downloadCsv, PartnerLabel, useToast, AlumniToggle, AlumniHidden,
} from '../components/ui'
import { Icon } from '../components/Icon'

const EMPTY = {
  q: '', partner_id: [], country_id: [], from: '', to: '', year: null, highlighted: null,
  include_alumni: false,
}

/** Thirty days back, as a date the API's `from` filter takes. */
const thirtyDaysAgo = () => {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return toIso(d)
}

/**
 * Filters can arrive in the URL, so a link from elsewhere can open this page already
 * narrowed. Written the same way as the Updates list's own `fromUrl`, and for the same
 * reason: anything unrecognised is dropped rather than passed to the API.
 *
 * Read once, into the initial state. The filters are a control after that, and a URL
 * that kept overwriting them would fight the person using them.
 */
function fromUrl(params) {
  const ids = (k) => {
    const raw = params.get(k)
    if (!raw) return []
    return raw.split(',').map((s) => Number(s)).filter((n) => Number.isInteger(n) && n > 0)
  }
  const year = params.get('year')
  return {
    ...EMPTY,
    q: params.get('q') ?? '',
    partner_id: ids('partner_id'),
    country_id: ids('country_id'),
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    year: year && /^\d{4}$/.test(year) ? year : null,
    highlighted: ['0', '1'].includes(params.get('highlighted')) ? params.get('highlighted') : null,
    include_alumni: params.get('include_alumni') === '1',
  }
}

export default function PrayerRequests({ recent = false }) {
  const toast = useToast()
  const [params] = useSearchParams()
  /* The seeding wins over the URL on this route, the URL having nothing in it — and it is
     computed here rather than held in a constant so a tab left open over a long weekend
     still asks for the last thirty days rather than the thirty that ended on Friday. */
  const [f, setF] = useState(() =>
    recent
      ? { ...EMPTY, highlighted: '1', from: thirtyDaysAgo(), include_alumni: true }
      : fromUrl(params)
  )
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [view, setView] = useViewMode('prayer-requests')
  const { sort, dir, onSort: sortBy } = useSort('published_on', 'desc', ['published_on'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  // Alumni are left out unless the toggle asks for them, as everywhere else. Sent
  // only when true so the default keeps the query string clean.
  const { include_alumni, ...filters } = f
  const query = useApi(
    ['prayer-requests', f, page, limit, sort, dir],
    `prayer-requests${qs({ ...filters, include_alumni: include_alumni ? 1 : null, page, limit, sort, dir })}`,
    { keepPreviousData: true }
  )

  // Every request these filters match, in this order — it used to be only the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('prayer-requests', {
        ...filters,
        include_alumni: include_alumni ? 1 : null,
        sort,
        dir,
      })
      downloadCsv('prayer-requests.csv', all.rows, [
        { key: 'published_on', label: 'Date' },
        { key: 's4ca_code', label: 'S4CA Code' },
        { key: 'partner_name', label: 'Partner Unit' },
        { key: 'country_name', label: 'Country' },
        { key: 'prayer_requests', label: 'Prayer Requests' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  const SORTS = [
    { key: 'published_on', label: 'Date' },
    { key: 's4ca_code', label: 'Partner Unit' },
    { key: 'country_name', label: 'Country' },
  ]

  const columns = [
    {
      key: 'published_on',
      label: 'Date',
      sortKey: 'published_on',
      width: 110,
      cellClass: 'nowrap',
      render: (r) => (
        <Link className="mono" to={`/newsletters/${r.id}`}>
          {r.published_on}
        </Link>
      ),
    },
    {
      key: 's4ca_code',
      label: 'Partner Unit',
      sortKey: 's4ca_code',
      width: 190,
      render: (r) => <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />,
    },
    { key: 'country_name', label: 'Country', sortKey: 'country_name', width: 140, cellClass: 'small' },
    {
      key: 'prayer_requests',
      label: 'Prayer Requests',
      cellClass: 'small',
      render: (r) => <PrayerText html={r.prayer_requests_html} text={r.prayer_requests} />,
    },
  ]

  return (
    <PageHeader
      title={recent ? 'Current Prayer Needs' : 'Prayer Requests'}
      sub={
        recent ? (
          'Prayer requests from highlighted updates in the last 30 days'
        ) : query.data ? (
          <>
            {fmtInt(query.data.total)} updates carry a request
            <AlumniHidden count={query.data.alumniHidden} noun="requests" />
          </>
        ) : (
          'Loading…'
        )
      }
      actions={
        <>
          <ViewToggle view={view} onView={setView} />
          <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
            ↓ CSV
          </button>
        </>
      }
    >
      <div className="col">
        {/* Above the filters rather than inside them, as on the other list pages: looking
            for a phrase is the commonest reason to open this one. Still the same `q`
            filter, so it still counts as one applied and the panel's ✕ still clears it. */}
        <SearchInput
          id="pr-q"
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="Words in a request…"
          aria-label="Search prayer requests"
        />
        <Filters id="prayer-requests" active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Partner Unit" htmlFor="pr-partner">
            <PartnerSelect inputId="pr-partner" multi value={f.partner_id} onChange={(v) => set({ partner_id: v })} />
          </Field>
          <Field label="Country" htmlFor="pr-country">
            <CountrySelect inputId="pr-country" multi value={f.country_id} onChange={(v) => set({ country_id: v })} />
          </Field>
          <Field label="Date range">
            <DateRangeField from={f.from} to={f.to} onFrom={(v) => set({ from: v })} onTo={(v) => set({ to: v })} />
          </Field>
          <Field label="Year" htmlFor="pr-year">
            <YearSelect inputId="pr-year" value={f.year} onChange={(v) => set({ year: v })} />
          </Field>
          <Field label="Highlighted need" htmlFor="pr-hl">
            <EnumSelect
              inputId="pr-hl"
              value={f.highlighted}
              onChange={(v) => set({ highlighted: v })}
              options={[
                { value: '1', label: 'On' },
                { value: '0', label: 'Off' },
              ]}
            />
          </Field>
          <AlumniToggle
            checked={f.include_alumni}
            onChange={(v) => set({ include_alumni: v })}
            where="requests"
          />
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching}>
            <DatePresets onPick={(from, to) => set({ from: from ?? '', to: to ?? '' })} />
          </FilterActions>
        </Filters>

        {query.isLoading && (
          <Card>
            <Loading />
          </Card>
        )}
        {query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}

        {query.data && !query.data.rows.length && (
          <Card>
            <Empty icon="prayer" title="No prayer requests match these filters" />
          </Card>
        )}

        {query.data?.rows.length && view === 'cards' ? (
          <SortBar options={SORTS} sort={sort} dir={dir} onSort={onSort} />
        ) : null}

        {query.data?.rows.length ? (
          view === 'cards' ? (
            <div className="grid grid-2">
              {query.data.rows.map((r) => (
                <Card key={r.id}>
                  <div className="row row-tight small" style={{ marginBottom: 8 }}>
                    <Link className="mono" to={`/newsletters/${r.id}`}>
                      {r.published_on}
                    </Link>
                    <span className="subtle">·</span>
                    <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
                    <span className="spacer" />
                    <span className="subtle">{r.country_name}</span>
                    {r.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /></Badge> : null}
                  </div>
                  <div className="nl-prayer">
                    <PrayerText html={r.prayer_requests_html} text={r.prayer_requests} />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <DataTable columns={columns} rows={query.data.rows} sort={sort} dir={dir} onSort={onSort} />
          )
        ) : null}

        {query.data?.total ? (
          <div className="card">
            <Pager
              page={query.data.page}
              pages={query.data.pages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={setPage}
              onLimit={(n) => {
                setLimit(n)
                setPage(1)
              }}
            />
          </div>
        ) : null}
      </div>
    </PageHeader>
  )
}
